import { SKILL_IDS } from "../constants.ts";
import type { SkillMastery } from "../mastery/skill-mastery.ts";
import type { SkillId } from "../model.ts";

export interface CurriculumPolicy {
  readonly version: string;
  readonly initialPracticeCount: number;
  readonly initialSkillId: SkillId;
  readonly orderedSkillIds: readonly SkillId[];
  readonly maintenanceSkillId: SkillId;
}

/** Ordered reachable opportunities, not a single goal held until mastery. Gates live in the planner. */
export function selectCurriculumOpportunities(
  attemptCount: number,
  skillMasteryById: ReadonlyMap<SkillId, SkillMastery>,
): readonly SkillId[] {
  const first = selectCurriculumTarget(attemptCount, skillMasteryById);
  if (attemptCount < ALPHA_CURRICULUM_POLICY.initialPracticeCount) return first;
  return [...new Set([...first, ...ALPHA_CURRICULUM_POLICY.orderedSkillIds])];
}

/** Provisional alpha route; these eight items are not a validated calibration protocol. */
export const ALPHA_CURRICULUM_POLICY: CurriculumPolicy = {
  version: "curriculum-alpha-v3",
  initialPracticeCount: 8,
  initialSkillId: SKILL_IDS.BASIC_TO_10,
  orderedSkillIds: [
    SKILL_IDS.PLUS_MINUS_1, SKILL_IDS.PLUS_MINUS_2,
    SKILL_IDS.DOUBLES, SKILL_IDS.NEAR_DOUBLES, SKILL_IDS.MAKE_10,
    SKILL_IDS.BASIC_TO_10, SKILL_IDS.TEEN_NO_BRIDGE,
    SKILL_IDS.CROSS_10_ADD, SKILL_IDS.CROSS_10_SUB,
  ],
  maintenanceSkillId: SKILL_IDS.MAINTAIN,
};

export function selectCurriculumTarget(
  attemptCount: number,
  skillMasteryById: ReadonlyMap<SkillId, SkillMastery>,
  policy: CurriculumPolicy = ALPHA_CURRICULUM_POLICY,
): readonly SkillId[] {
  if (!Number.isSafeInteger(attemptCount) || attemptCount < 0) throw new Error("Ongeldig aantal oefenpogingen.");
  if (attemptCount < policy.initialPracticeCount) return [policy.initialSkillId];
  return [policy.orderedSkillIds.find((skillId) => {
    const stage = skillMasteryById.get(skillId)?.currentStage;
    return stage !== "CAN_DO" && stage !== "KNOWS_IMMEDIATELY";
  }) ?? policy.maintenanceSkillId];
}
